import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import SocialLinks from '../components/SocialLinks';
import { trackEvent } from '../components/Analytics';

const PLAY_STORE_URL = 'https://get.bucktime.ca/android';
const APP_STORE_URL = 'https://get.bucktime.ca/ios';

export default function LandingPage() {
  const [searchParams] = useSearchParams();

  /* Scroll-snap between hero (state 1) and content (state 2) */
  useEffect(() => {
    const lp = document.querySelector('.landing-page');
    if (!lp) return;

    let isSnapping = false;
    let debounceTimer = null;
    let inputType = 'mouse';

    /* Detect input type for adaptive thresholds */
    const onTouch = () => { inputType = 'touch'; };
    const onWheel = () => { inputType = 'mouse'; };
    lp.addEventListener('touchstart', onTouch, { passive: true });
    lp.addEventListener('wheel', onWheel, { passive: true });

    const checkAndSnap = () => {
      if (isSnapping) return;
      const st = lp.scrollTop;
      const vh = window.innerHeight;

      /* Binary snap: one threshold decides back vs down — no in-between
         where the hero h1 can be half-hidden behind the compact header */
      const threshold = inputType === 'touch' ? vh * 0.30 : vh * 0.35;

      if (st >= threshold && st < vh) {
        /* Snap DOWN to "How it works" */
        isSnapping = true;
        setTimeout(() => {
          const how = document.getElementById('how-it-works');
          const headerEl = document.querySelector('.site-header');
          if (how && headerEl) {
            const compactH = headerEl.getBoundingClientRect().height;
            const targetScroll = how.offsetTop - compactH - 8;
            lp.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
          }
          setTimeout(() => { isSnapping = false; }, 700);
        }, 450);
      } else if (st > 0 && st < threshold) {
        /* Snap BACK to hero */
        isSnapping = true;
        lp.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => { isSnapping = false; }, 700);
      }
    };

    const onScroll = () => {
      if (!isSnapping) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(checkAndSnap, 150);
      }
    };

    lp.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      lp.removeEventListener('scroll', onScroll);
      lp.removeEventListener('touchstart', onTouch);
      lp.removeEventListener('wheel', onWheel);
      clearTimeout(debounceTimer);
    };
  }, []);

  const handlePlayClick = (src) => {
    trackEvent(`play-store-click-${src}`, '/');
    window.open(`${PLAY_STORE_URL}?src=${src}`, '_blank');
  };

  const handleAppStoreClick = (src) => {
    trackEvent(`app-store-click-${src}`, '/');
    window.open(`${APP_STORE_URL}?src=${src}`, '_blank');
  };

  return (
    <div className="landing-page" data-testid="landing-page">
      <SiteHeader />

      {/* ───── HERO ───── */}
      <section className="lp-hero" data-testid="hero-section">
        <div className="container lp-hero-inner">
          <div className="lp-hero-text">
            <h1 className="lp-hero-h1">
              The screen-time app that pays kids to put the phone down.
            </h1>
            <p className="lp-hero-sub">
              The day's money is your child's from the morning. The apps you choose are the only thing that spends it — so what they don't spend, they keep.
            </p>
            <div className="lp-hero-btns">
              <button className="lp-play-badge" onClick={() => handlePlayClick('hero')} data-testid="hero-play-btn">
                <img src="/google-play-badge.png" alt="Get it on Google Play" className="play-badge-img" />
              </button>
              <button className="lp-play-badge" onClick={() => handleAppStoreClick('hero')} data-testid="hero-appstore-btn">
                <img src="/app-store-badge.svg" alt="Download on the App Store" className="play-badge-img" />
              </button>
              <a href="#how-it-works" className="lp-cta-secondary" data-testid="hero-how-btn">
                How it works
              </a>
            </div>
          </div>
          <div className="lp-hero-visual">
            <img
              src="/family-hero.png"
              alt="BuckTime! family"
              className="lp-hero-img"
              data-testid="hero-family-image"
            />
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="lp-section lp-how" id="how-it-works" data-testid="how-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">How it works</h2>
          <div className="lp-steps">
            <div className="lp-step" data-testid="step-1">
              <span className="lp-step-num">1</span>
              <h3 className="lp-step-title">Set what a day is worth.</h3>
              <p className="lp-step-desc">You decide the daily amount, and how much time on those apps costs nothing at all.</p>
            </div>
            <div className="lp-step" data-testid="step-2">
              <span className="lp-step-num">2</span>
              <h3 className="lp-step-title">Your child starts the day holding it.</h3>
              <p className="lp-step-desc">The full amount is theirs from the morning.</p>
            </div>
            <div className="lp-step" data-testid="step-3">
              <span className="lp-step-num">3</span>
              <h3 className="lp-step-title">The apps you picked spend it.</h3>
              <p className="lp-step-desc">Every minute on one draws it down. Nothing is blocked — it's their call.</p>
            </div>
            <div className="lp-step" data-testid="step-4">
              <span className="lp-step-num">4</span>
              <h3 className="lp-step-title">They keep the rest.</h3>
              <p className="lp-step-desc">Pay it out however suits your family.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── THE CHOICE ───── */}
      <section className="lp-section lp-choice" data-testid="choice-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">The choice, not the restriction</h2>
          <div className="lp-choice-cols">
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">Nothing is forced.</h3>
              <p className="lp-section-body">
                BuckTime! never blocks an app, never locks a device, and never takes the phone away. Your child can open anything they like — it just costs them.
              </p>
            </div>
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">That's why it works.</h3>
              <p className="lp-section-body">
                A limit gets argued with. A choice gets made. When the money is already theirs, putting the phone down stops being your instruction and starts being their decision.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── SCREEN-TIME GOALS ───── */}
      <section className="lp-section lp-goals" data-testid="goals-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Screen-time goals</h2>
          <div className="lp-choice-cols">
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">A goal that actually moves.</h3>
              <p className="lp-section-body">
                Set where your child is starting from and where you'd like them to get to, and BuckTime! steps them down gradually — not in one jump they'll resent.
              </p>
            </div>
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">You see whether it's working.</h3>
              <p className="lp-section-body">
                The parent dashboard shows their typical day against the goal, how many days they've met it, and whether the trend is going the right way. If there isn't enough measured yet to say, it tells you that instead of guessing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── THREE JARS ───── */}
      <section className="lp-section lp-jars" data-testid="jars-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">The Three Jars</h2>
          <p className="lp-section-body">
            <strong>Spend, Save, Give.</strong> What your child keeps is split across three jars, so the money teaches something on the way out. You choose the split.
          </p>
          <p className="lp-section-body">
            On the Family plan you can match what they save — money you add to their Save jar to reward the habit, never cashed out as pocket money.
          </p>
        </div>
      </section>

      {/* ───── BUCK & BUCK SHOP ───── */}
      <section className="lp-section lp-buck" data-testid="buck-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Buck and the Buck Shop</h2>
          <div className="lp-choice-cols">
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">Meet Buck.</h3>
              <p className="lp-section-body">
                Every child gets a mascot, and coins for the habits worth building — a clean day, a streak, a savings goal reached, a lesson finished.
              </p>
            </div>
            <div className="lp-choice-block">
              <h3 className="lp-choice-heading">Coins buy Buck's wardrobe.</h3>
              <p className="lp-section-body">
                Hats, shades, shirts, boots and flags in the Buck Shop. Free on every plan; the Family plan unlocks the rare, epic and legendary pieces.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── MONEY SCHOOL ───── */}
      <section className="lp-section lp-school" data-testid="school-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Money School</h2>
          <p className="lp-section-body">
            <strong>The lessons under the money.</strong> Money School, Word Bank and Smart$ turn what your child is doing into what they understand — short lessons, real vocabulary, and medallions for finishing them. Included free.
          </p>
        </div>
      </section>

      {/* ───── GET ACTIVE ───── */}
      <section className="lp-section lp-active" data-testid="active-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Get active</h2>
          <p className="lp-section-note">Family plan</p>
          <p className="lp-section-body">
            <strong>Moving pays.</strong> On the Family plan, steps and active minutes earn on their own — so screen-free time becomes real time outside, not just time away from a screen.
          </p>
        </div>
      </section>

      {/* ───── FOR PARENTS ───── */}
      <section className="lp-section lp-parents" data-testid="parents-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">For parents</h2>
          <div className="lp-parent-items">
            <div className="lp-parent-item" data-testid="parent-0">
              <h3 className="lp-choice-heading">One dashboard, every child.</h3>
              <p className="lp-section-body">What today looks like, what they're holding, and whether their screen time is actually coming down.</p>
            </div>
            <div className="lp-parent-item" data-testid="parent-1">
              <h3 className="lp-choice-heading">Add the other parent.</h3>
              <p className="lp-section-body">Shared approvals and the same view, on the Family plan.</p>
            </div>
            <div className="lp-parent-item" data-testid="parent-2">
              <h3 className="lp-choice-heading">Change anything, any time.</h3>
              <p className="lp-section-body">The daily amount, the goal, which apps count.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── PRICING ───── */}
      <section className="lp-section lp-pricing" data-testid="pricing-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Pricing</h2>
          <div className="lp-pricing-cards">
            <div className="lp-price-card" data-testid="price-basic">
              <h3 className="lp-price-name">Basic — free.</h3>
              <p className="lp-section-body">
                One child, one device. Full monitoring, daily earning, cash payouts, Three Jars, Money School, medallions, and the Buck Shop's common collection.
              </p>
            </div>
            <div className="lp-price-card lp-price-card-featured" data-testid="price-family">
              <h3 className="lp-price-name">Family — CA$6.99/month, or CA$59.99/year <span className="lp-price-save">(save ~28%)</span></h3>
              <p className="lp-section-body">
                Everything in Basic, plus unlimited children and devices, a second parent, fitness rewards, the full Buck Shop, jar matching, Save-jar payouts, and advanced insights with a weekly digest.
              </p>
              <p className="lp-price-renew"><em>Subscriptions renew until cancelled. Cancel any time in Google Play or the App Store.</em></p>
            </div>
          </div>
          <div className="lp-hero-btns" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
            <button className="lp-play-badge" onClick={() => handlePlayClick('pricing')} data-testid="pricing-play-btn">
              <img src="/google-play-badge.png" alt="Get it on Google Play" className="play-badge-img" />
            </button>
            <button className="lp-play-badge" onClick={() => handleAppStoreClick('pricing')} data-testid="pricing-appstore-btn">
              <img src="/app-store-badge.svg" alt="Download on the App Store" className="play-badge-img" />
            </button>
          </div>
        </div>
      </section>

      {/* ───── PAYOUTS ───── */}
      <section className="lp-section lp-payouts" data-testid="payouts-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Payouts</h2>
          <p className="lp-section-body">
            <strong>You pay your child — we just keep the count.</strong> BuckTime! never holds or moves anyone's money. What your child keeps is recorded in the ledger; you settle up in cash, by e-Transfer from your own bank, or however you already do it. Spend-jar payouts have a $20 minimum. Save-jar cash-outs have no minimum.
          </p>
        </div>
      </section>

      {/* ───── PRIVACY ───── */}
      <section className="lp-section lp-privacy" data-testid="privacy-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Privacy</h2>
          <p className="lp-section-body">
            BuckTime! is built for families and complies with COPPA, GDPR and PIPEDA. We never sell your data and never share it with advertisers. Children's profiles are created and controlled only by the parent, and you can delete your account and all of its data at any time from inside the app.
          </p>
        </div>
      </section>

      {/* ───── ANDROID PERMISSIONS ───── */}
      <section className="lp-section lp-permissions" data-testid="permissions-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">How BuckTime! uses the Accessibility Service</h2>
          <div className="lp-permissions-text">
            <p>
              BuckTime! uses Android's AccessibilityService API to enforce the focus rules you set on your own child's device. You switch it on yourself during setup, and you can switch it off at any time in Android Settings.
            </p>
            <p>
              While a Buck Break is running — and only then — it does two things:
            </p>
            <ul>
              <li>Reads the address bar of a supported web browser that is open on screen, so websites you have approved keep working even when the browser itself is paused. It reads the address only — never the page, and never what is typed into it.</li>
              <li>Detects split-screen and multi-window, so a paused app cannot be hidden beside an allowed one.</li>
            </ul>
            <p>
              Outside a Buck Break it reads nothing at all. It never reads messages, passwords, or anything you type, and it collects nothing for advertising.
            </p>
          </div>
        </div>
      </section>

      {/* ───── TESTIMONIALS ───── */}
      <section className="lp-section" data-testid="testimonials-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">What families are saying</h2>
          <p className="lp-section-sub">Real reviews from the App Store.</p>
          <div className="testimonial-grid">
            <div className="testimonial-card" data-testid="testimonial-1">
              <div className="testimonial-stars">{'★'.repeat(5)}</div>
              <h3 className="testimonial-title">Love It!!</h3>
              <p className="testimonial-text">
                After trying a few screen time apps, I found my kids would get angry and resent me when they ran out of minutes. I tried this app out and was surprised how it changed their phone use. BuckTime! gives them a choice and I really like that. A couple Bucks (pun intended!) a day to get off their phone is worth it. I think the Money School and Dough'Nate are a great idea. My son sent $20 to the Humane Canada charity and he felt so proud afterward.
              </p>
              <p className="testimonial-source">App Store Review</p>
            </div>
            <div className="testimonial-card" data-testid="testimonial-2">
              <div className="testimonial-stars">{'★'.repeat(5)}</div>
              <h3 className="testimonial-title">This actually works!</h3>
              <p className="testimonial-text">
                We used BuckTime! as a way to link pocket money to positive outcomes and incentives. We loved the jars feature (Spend, Save and Dough'Nate) plus the huge list of charities he could choose from. Best of all he felt so motivated and positive about the time spent off his phone. Because it wasn't punitive, he felt really inspired to go and spend time reading books, playing sports with friends.
              </p>
              <p className="testimonial-source">App Store Review</p>
            </div>
            <div className="testimonial-card" data-testid="testimonial-3">
              <div className="testimonial-stars">{'★'.repeat(5)}</div>
              <h3 className="testimonial-title">Big fans!</h3>
              <p className="testimonial-text">
                BuckTime! helped us achieve what seemed impossible: reducing the time our kids spend on their devices, without major meltdowns. It's been a great way for them to earn money, while showing them the benefit of making better choices. A bonus is that the app looks cool, and is very easy to use!
              </p>
              <p className="testimonial-source">App Store Review</p>
            </div>
            <div className="testimonial-card" data-testid="testimonial-4">
              <div className="testimonial-stars">{'★'.repeat(5)}</div>
              <h3 className="testimonial-title">Cool App!</h3>
              <p className="testimonial-text">
                My parents wanted me to try this app out. I didn't think I would use it, but once I started it was really cool. I actually started using my phone a lot less and made some pretty good money. I even donated to a charity. The mascot store is cool too, sick app.
              </p>
              <p className="testimonial-source">App Store Review</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FOLLOW US ───── */}
      <section className="lp-section lp-follow" data-testid="follow-section">
        <div className="container lp-section-inner">
          <h2 className="lp-follow-h2">Follow the journey</h2>
          <SocialLinks size="lg" />
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="lp-final" data-testid="final-cta-section">
        <div className="container lp-final-inner">
          <h2 className="lp-final-h2">
            Ready to let your child make the choice?
          </h2>
          <div className="lp-hero-btns" style={{ justifyContent: 'center' }}>
            <button className="lp-play-badge" onClick={() => handlePlayClick('footer')} data-testid="bottom-play-btn">
              <img src="/google-play-badge.png" alt="Get it on Google Play" className="play-badge-img" />
            </button>
            <button className="lp-play-badge" onClick={() => handleAppStoreClick('footer')} data-testid="bottom-appstore-btn">
              <img src="/app-store-badge.svg" alt="Download on the App Store" className="play-badge-img" />
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
